/**
 * Stage I runway flask — letterbox placement + explicit collider bounds.
 *
 * Edit **`FALL_FLASK_COLLIDERS`**: neck rect + body trapezoid in **normalized letterbox**
 * space (origin top-left of the `flask.png` draw rect, units 0–1 of drawW / drawH).
 * World position comes from runway placement + player `cx`; size follows display scale.
 *
 * Per-run overrides: `runConfig.fallFlaskColliders` (or legacy `runConfig.fallFlaskShape` fractions).
 */
import * as C from "../config/constants.js";

/** Mouth band height as a fraction of {@link FallFlaskNeckCollider#h}. */
export const FALL_MOUTH_HEIGHT_FRAC_OF_NECK = 0.1;

/**
 * @typedef {Object} FallFlaskMouthCollider
 * @property {number} y Top (rim), fraction of drawH from drawY.
 * @property {number} w Collider width, fraction of drawW.
 * @property {number} h Mouth band height, fraction of drawH (Matter body + shape guide).
 * @property {number} [x] Horizontal center offset from flask `cx`, fraction of drawW (default 0).
 * @property {number} [hFracOfNeck] Legacy fallback when `h` is omitted.
 */

/**
 * @typedef {Object} FallFlaskCollectBand
 * @property {number} y Top of suction/collect band, fraction of drawH from drawY.
 * @property {number} w Collect width, fraction of drawW.
 * @property {number} [x] Horizontal center offset from flask `cx`, fraction of drawW (default 0).
 * @property {number} h Band height, fraction of drawH (`checkSuction` only — no Matter body).
 */

/**
 * @typedef {Object} FallFlaskNeckCollider
 * @property {number} y Top (below mouth band), fraction of drawH from drawY.
 * @property {number} w Neck collider width, fraction of drawW.
 * @property {number} [x] Horizontal center offset from flask `cx`, fraction of drawW (default 0).
 * @property {number} h Full neck span from rim to shoulder, fraction of drawH (includes mouth band).
 */

/**
 * @typedef {Object} FallFlaskTrapCollider
 * @property {number} y Top of trapezoid (shoulder), fraction of drawH from drawY.
 * @property {number} topHalfW Half-width at top, fraction of drawW (centered on flask `cx`).
 * @property {number} bottomHalfW Half-width at bottom, fraction of drawW.
 * @property {number} h Trapezoid height, fraction of drawH.
 */

/**
 * @typedef {Object} FallFlaskColliderTuning
 * @property {FallFlaskMouthCollider} mouth Single Matter rect (`flask_mouth`).
 * @property {FallFlaskCollectBand} collect Geometry-only suction band (dev overlay: green).
 * @property {FallFlaskNeckCollider} neck
 * @property {FallFlaskTrapCollider} trap
 */

/** @type {FallFlaskColliderTuning} */
export const FALL_FLASK_COLLIDERS = {
  collect: { y: 0.02, w: 0.3, h: 0.03 },
  mouth: { y: 0.025, w: 0.4, h: 0.08 },
  neck: { y: 0.05, w: 0.3, h: 0.25 },
  trap: { y: 0.35, topHalfW: 0.15, bottomHalfW: 0.45, h: 0.56 },
};

/**
 * @deprecated Use {@link FALL_FLASK_COLLIDERS}. Kept for docs / legacy `fallFlaskShape` keys.
 * @type {{ neckHalfWidthFrac: number, neckHeightFrac: number, floorHalfWidthFrac: number, bottomTrimFrac: number, topTrimFrac: number }}
 */
export const FALL_FLASK_SHAPE = {
  neckHalfWidthFrac: 0.13,
  neckHeightFrac: 0.35,
  floorHalfWidthFrac: 0.44,
  bottomTrimFrac: 0.135,
  topTrimFrac: 0,
};

var COLLIDER_MOUTH_KEYS = ["y", "w", "h", "x", "hFracOfNeck"];
var COLLIDER_COLLECT_KEYS = ["y", "w", "h", "x"];
var COLLIDER_NECK_KEYS = Object.keys(FALL_FLASK_COLLIDERS.neck);
var COLLIDER_TRAP_KEYS = Object.keys(FALL_FLASK_COLLIDERS.trap);

/**
 * Mouth band height in letterbox drawH fractions.
 * @param {FallFlaskMouthCollider} mo
 * @param {FallFlaskNeckCollider} nk
 */
export function resolveFallMouthColliderHFrac(mo, nk) {
  var mouth = mo || FALL_FLASK_COLLIDERS.mouth;
  var neck = nk || FALL_FLASK_COLLIDERS.neck;
  if (mouth.h != null && mouth.h > 0) return mouth.h;
  return neck.h * (mouth.hFracOfNeck != null
    ? mouth.hFracOfNeck
    : FALL_MOUTH_HEIGHT_FRAC_OF_NECK);
}

export function centeredRectWorld(cx, draw, yFrac, wFrac, hFrac, xFrac) {
  var dw = draw.drawW;
  var dh = draw.drawH;
  var wPx = wFrac * dw;
  var hPx = hFrac * dh;
  var top = draw.drawY + yFrac * dh;
  var centerX = cx + (xFrac != null && isFinite(Number(xFrac)) ? Number(xFrac) * dw : 0);
  var left = centerX - wPx / 2;
  return {
    left: left,
    right: left + wPx,
    top: top,
    bottom: top + hPx,
    halfW: wPx / 2,
    height: hPx,
    centerX: centerX,
  };
}

function readNumber(value, fallback) {
  var n = Number(value);
  return isFinite(n) ? n : fallback;
}

/**
 * Legacy fraction profile → {@link FallFlaskColliderTuning}.
 * @param {typeof FALL_FLASK_SHAPE} [shape]
 * @returns {FallFlaskColliderTuning}
 */
export function colliderTuningFromLegacyShape(shape) {
  var s = shape || FALL_FLASK_SHAPE;
  var top = readNumber(s.topTrimFrac, 0);
  var neckH = readNumber(s.neckHeightFrac, 0.35);
  var neckHalf = readNumber(s.neckHalfWidthFrac, 0.13);
  var bottomTrim = readNumber(s.bottomTrimFrac, 0.135);
  var trapY = s.bodyTopFrac != null && s.neckHeightFrac == null
    ? readNumber(s.bodyTopFrac, top + neckH)
    : top + neckH;
  var mouthW = neckHalf * 2;
  var neckW = mouthW * 0.85;
  var mouthH = DEFAULT_MOUTH_H_FRAC;
  return {
    mouth: { y: top, w: mouthW, h: mouthH },
    collect: { y: top, w: mouthW * 0.92, h: 0.03 },
    neck: {
      y: top,
      w: neckW,
      h: neckH,
    },
    trap: {
      y: trapY,
      topHalfW: neckHalf,
      bottomHalfW: readNumber(s.floorHalfWidthFrac, 0.44),
      h: Math.max(0, 1 - bottomTrim - trapY),
    },
  };
}

function patchColliderSection(target, patch, keys) {
  if (!patch || typeof patch !== "object") return;
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (patch[key] != null) target[key] = readNumber(patch[key], target[key]);
  }
}

/**
 * @param {object} [runConfig]
 * @returns {FallFlaskColliderTuning}
 */
export function getFallFlaskColliderTuning(runConfig) {
  var rc = runConfig || {};
  var base;
  if (rc.fallFlaskShape && typeof rc.fallFlaskShape === "object") {
    base = colliderTuningFromLegacyShape(Object.assign({}, FALL_FLASK_SHAPE, rc.fallFlaskShape));
  } else {
    base = {
      mouth: Object.assign({}, FALL_FLASK_COLLIDERS.mouth),
      collect: Object.assign({}, FALL_FLASK_COLLIDERS.collect),
      neck: Object.assign({}, FALL_FLASK_COLLIDERS.neck),
      trap: Object.assign({}, FALL_FLASK_COLLIDERS.trap),
    };
  }
  if (!base.mouth) {
    base.mouth = Object.assign({}, FALL_FLASK_COLLIDERS.mouth, {
      y: base.neck.y,
      w: base.neck.w / 0.85,
    });
  }
  if (!base.collect) {
    base.collect = Object.assign({}, FALL_FLASK_COLLIDERS.collect);
  }
  if (rc.fallFlaskColliders && typeof rc.fallFlaskColliders === "object") {
    patchColliderSection(base.mouth, rc.fallFlaskColliders.mouth, COLLIDER_MOUTH_KEYS);
    patchColliderSection(base.collect, rc.fallFlaskColliders.collect, COLLIDER_COLLECT_KEYS);
    patchColliderSection(base.neck, rc.fallFlaskColliders.neck, COLLIDER_NECK_KEYS);
    patchColliderSection(base.trap, rc.fallFlaskColliders.trap, COLLIDER_TRAP_KEYS);
  }
  return base;
}

/** @deprecated Use {@link getFallFlaskColliderTuning}. */
export function getFallFlaskShapeTuning(runConfig) {
  return getFallFlaskColliderTuning(runConfig);
}

export function getFlaskImageAspectRatio() {
  return C.FLASK_IMAGE_ASPECT_RATIO != null ? Number(C.FLASK_IMAGE_ASPECT_RATIO) : 383 / 256;
}

/**
 * Same letterboxing as `FlaskRenderer` / `letterboxFlaskLayout`.
 * @returns {{ drawX: number, drawY: number, drawW: number, drawH: number }}
 */
export function letterboxFlaskInRect(x, y, w, h, aspect) {
  if (!(w > 0) || !(h > 0)) {
    return { drawX: x, drawY: y, drawW: w, drawH: h };
  }
  var a = aspect > 0 ? aspect : getFlaskImageAspectRatio();
  var drawW = w;
  var drawH = w * a;
  if (drawH > h) {
    drawH = h;
    drawW = h / a;
  }
  return {
    drawX: x + (w - drawW) / 2,
    drawY: y + (h - drawH) / 2,
    drawW: drawW,
    drawH: drawH,
  };
}

/**
 * Placement box for the runway flask (matches `FallPhysics` / `FallRenderer` `_getFlaskDims`).
 * @param {number} runHeight
 * @param {object} [scale]
 */
export function getRunwayFlaskPlacement(runHeight, scale) {
  var flaskW = scale && scale.runwayFlaskDisplayWidth ? scale.runwayFlaskDisplayWidth : 120;
  var flaskH = scale && scale.runwayFlaskDisplayHeight ? scale.runwayFlaskDisplayHeight : 120;
  var topFromBottom = C.FLASK_TOP_FROM_BOTTOM_RATIO != null
    ? C.FLASK_TOP_FROM_BOTTOM_RATIO : 0.24;
  var desiredTopY = runHeight * (1 - topFromBottom);
  var bottomGap = runHeight * 0.018;
  var flaskTopY = Math.min(desiredTopY, runHeight - flaskH - bottomGap);
  if (flaskTopY < 0) flaskTopY = 0;
  return { boxW: flaskW, boxH: flaskH, boxTopY: flaskTopY };
}

/**
 * Letterboxed draw rect centered on `cx`.
 * @param {number} cx
 * @param {number} runHeight
 * @param {object} [scale]
 */
export function getRunwayFlaskDrawLayout(cx, runHeight, scale) {
  var place = getRunwayFlaskPlacement(runHeight, scale);
  var boxX = cx - place.boxW / 2;
  var lb = letterboxFlaskInRect(
    boxX, place.boxTopY, place.boxW, place.boxH, getFlaskImageAspectRatio()
  );
  return Object.assign({ cx: cx }, lb);
}

/**
 * World-space flask geometry from explicit collider tuning + letterbox draw rect.
 * @param {number} cx
 * @param {{ drawX: number, drawY: number, drawW: number, drawH: number }} draw
 * @param {FallFlaskColliderTuning} tuning
 * @param {number} [wallThicknessPx]
 */
export function geomFromFallFlaskColliderTuning(cx, draw, tuning, wallThicknessPx) {
  var W = wallThicknessPx != null && isFinite(Number(wallThicknessPx))
    ? Number(wallThicknessPx) : 28;
  var dw = draw.drawW;
  var dh = draw.drawH;
  var dx = draw.drawX;
  var dy = draw.drawY;
  var mo = tuning.mouth || FALL_FLASK_COLLIDERS.mouth;
  var nk = tuning.neck;
  var tr = tuning.trap;

  var rimYFrac = nk.y != null ? nk.y : mo.y;
  var mouthHFrac = resolveFallMouthColliderHFrac(mo, nk);
  var mouthRect = centeredRectWorld(
    cx, draw, mo.y != null ? mo.y : rimYFrac, mo.w, mouthHFrac, mo.x
  );
  var neckRect = centeredRectWorld(cx, draw, rimYFrac, nk.w, nk.h, nk.x);
  var col = tuning.collect || FALL_FLASK_COLLIDERS.collect;
  var collectRect = centeredRectWorld(cx, draw, col.y, col.w, col.h, col.x);

  var T = mouthRect.top;
  var mouthBottom = mouthRect.bottom;
  var neckBottom = neckRect.bottom;
  var bodyTop = dy + tr.y * dh;
  var bodyBottom = bodyTop + tr.h * dh;
  var topHalfW = tr.topHalfW * dw;
  var bottomHalfW = tr.bottomHalfW * dw;
  var floorLeft = cx - bottomHalfW;
  var floorRight = cx + bottomHalfW;
  var shoulderLeft = cx - topHalfW;
  var shoulderRight = cx + topHalfW;
  var mouthHalfW = mouthRect.halfW;
  var neckHalfW = neckRect.halfW;
  var mouthLeft = mouthRect.left;
  var mouthRight = mouthRect.right;
  var neckLeft = neckRect.left;
  var neckRight = neckRect.right;

  return {
    flaskType: "erlenmeyer",
    cx: cx,
    cy: (bodyTop + bodyBottom) / 2,
    T: T,
    mouthBottom: mouthBottom,
    mouthLeft: mouthLeft,
    mouthRight: mouthRight,
    mouthHalfWidth: mouthHalfW,
    neckBottom: neckBottom,
    bodyTop: bodyTop,
    bodyBottom: bodyBottom,
    neckHalfWidth: neckHalfW,
    floorHalfWidth: bottomHalfW,
    trapTopHalfWidth: topHalfW,
    neckLeft: neckLeft,
    neckRight: neckRight,
    shoulderLeft: shoulderLeft,
    shoulderRight: shoulderRight,
    floorLeft: floorLeft,
    floorRight: floorRight,
    drawX: dx,
    drawY: dy,
    drawW: dw,
    drawH: dh,
    sphereR: Math.max(1, Math.min(bottomHalfW, (bodyBottom - bodyTop) / 2)),
    ellipseRx: mouthHalfW,
    ellipseRy: Math.max(1, (bodyBottom - bodyTop) / 2),
    mouthY: (T + mouthBottom) / 2,
    collectLeft: collectRect.left,
    collectRight: collectRect.right,
    collectTop: collectRect.top,
    collectBottom: collectRect.bottom,
    collectHalfWidth: collectRect.halfW,
    W: W,
    colliderTuning: tuning,
  };
}

/**
 * Runway flask geometry for Matter colliders, suction rim, and debug guides.
 * @param {number} cx
 * @param {number} runHeight
 * @param {object} [scale]
 * @param {number} [wallThicknessPx]
 * @param {object} [runConfig]
 */
export function buildRunwayFlaskPhysicsGeometry(cx, runHeight, scale, wallThicknessPx, runConfig) {
  var draw = getRunwayFlaskDrawLayout(cx, runHeight, scale);
  var tuning = getFallFlaskColliderTuning(runConfig);
  return geomFromFallFlaskColliderTuning(cx, draw, tuning, wallThicknessPx);
}

/**
 * Horizontal half-width of the Fall mouth opening (`FALL_FLASK_COLLIDERS.mouth.w`), px.
 * Prefer live `flaskGeometry` edges; else letterboxed colliders when scale has
 * `runwayFlaskDisplayWidth`; else legacy `scale.flaskMouthWidth`.
 * @param {{
 *   flaskGeometry?: { mouthLeft?: number, mouthRight?: number, mouthHalfWidth?: number, neckHalfWidth?: number },
 *   scale?: object,
 *   runWidth?: number,
 *   runHeight?: number,
 *   runConfig?: object,
 *   wallThicknessPx?: number,
 *   cx?: number,
 * }} [params]
 * @returns {number|null}
 */
export function getFallMouthHalfWidthPx(params) {
  params = params || {};
  var geom = params.flaskGeometry;
  if (geom && geom.mouthLeft != null && geom.mouthRight != null) {
    var fromEdges = (geom.mouthRight - geom.mouthLeft) / 2;
    if (isFinite(fromEdges) && fromEdges > 0) return Math.max(1, fromEdges);
  }
  if (geom && geom.mouthHalfWidth != null && isFinite(geom.mouthHalfWidth) && geom.mouthHalfWidth > 0) {
    return Math.max(1, geom.mouthHalfWidth);
  }
  var scale = params.scale;
  if (params.runHeight != null && scale && scale.runwayFlaskDisplayWidth != null) {
    var cx = params.cx != null && isFinite(params.cx)
      ? params.cx
      : (params.runWidth != null ? params.runWidth / 2 : 0);
    var built = buildRunwayFlaskPhysicsGeometry(
      cx,
      params.runHeight,
      scale,
      params.wallThicknessPx,
      params.runConfig
    );
    if (built.mouthLeft != null && built.mouthRight != null) {
      var hw = (built.mouthRight - built.mouthLeft) / 2;
      if (isFinite(hw) && hw > 0) return Math.max(1, hw);
    }
    if (built.mouthHalfWidth > 0) return Math.max(1, built.mouthHalfWidth);
  }
  if (scale && scale.flaskMouthWidth != null && isFinite(scale.flaskMouthWidth)) {
    return Math.max(1, scale.flaskMouthWidth / 2);
  }
  if (geom && geom.neckHalfWidth != null && geom.neckHalfWidth > 0) {
    return Math.max(1, geom.neckHalfWidth);
  }
  return null;
}

/**
 * Erlenmeyer outline in world space (sprite cracks / visual fallback).
 * @param {number} cx
 * @param {{ drawX: number, drawY: number, drawW: number, drawH: number }} draw
 * @param {FallFlaskColliderTuning|typeof FALL_FLASK_SHAPE} [shape]
 */
export function computeFallFlaskOutlineGeom(cx, draw, shape) {
  var tuning = shape && shape.neck && shape.trap
    ? shape
    : colliderTuningFromLegacyShape(shape || FALL_FLASK_SHAPE);
  return geomFromFallFlaskColliderTuning(cx, draw, tuning, null);
}

function outwardNormal2D(dx, dy) {
  var len = Math.sqrt(dx * dx + dy * dy);
  if (!(len > 1e-6)) return { x: 0, y: -1 };
  return { x: dy / len, y: -dx / len };
}

function addOffset(p, n, dist) {
  return { x: p.x + n.x * dist, y: p.y + n.y * dist };
}

function averageNormal(a, b) {
  var x = a.x + b.x;
  var y = a.y + b.y;
  var len = Math.sqrt(x * x + y * y);
  if (!(len > 1e-6)) return a;
  return { x: x / len, y: y / len };
}

/**
 * Inner trapezoid + outward-offset ring for the open-topped body shell.
 * @returns {{ inner: { x: number, y: number }[], outer: { x: number, y: number }[] }|null}
 */
function buildFallFlaskBodyShellRings(geom, wallThicknessPx) {
  if (!geom) return null;
  var W = Math.max(1, readNumber(wallThicknessPx, geom.W != null ? geom.W : 28));
  var h = W / 2;
  var topHalfW = geom.trapTopHalfWidth != null
    ? geom.trapTopHalfWidth
    : (geom.neckRight - geom.neckLeft) / 2;
  var nL = geom.shoulderLeft != null ? geom.shoulderLeft : geom.cx - topHalfW;
  var nR = geom.shoulderRight != null ? geom.shoulderRight : geom.cx + topHalfW;
  var fL = geom.floorLeft;
  var fR = geom.floorRight;
  var B = geom.bodyBottom;
  var bodyTop = geom.bodyTop != null ? geom.bodyTop : geom.neckBottom;

  var inner = [
    { x: nL, y: bodyTop },
    { x: fL, y: B },
    { x: fR, y: B },
    { x: nR, y: bodyTop },
  ];
  var nUp = { x: 0, y: -1 };
  var nE1 = outwardNormal2D(inner[1].x - inner[0].x, inner[1].y - inner[0].y);
  var nE2 = outwardNormal2D(inner[2].x - inner[1].x, inner[2].y - inner[1].y);
  var nE3 = outwardNormal2D(inner[3].x - inner[2].x, inner[3].y - inner[2].y);

  var outer = [
    addOffset(inner[0], averageNormal(nUp, nE1), h),
    addOffset(inner[1], averageNormal(nE1, nE2), h),
    addOffset(inner[2], averageNormal(nE2, nE3), h),
    addOffset(inner[3], averageNormal(nE3, nUp), h),
  ];
  return { inner: inner, outer: outer };
}

/**
 * Open-topped trapezoid body shell outline (floor + sloped sides). Concave — do not
 * pass this ring to `Bodies.fromVertices` (Matter logs poly-decomp and uses a solid hull).
 * @param {ReturnType<typeof geomFromFallFlaskColliderTuning>} geom
 * @param {number} wallThicknessPx
 * @returns {{ x: number, y: number }[]}
 */
export function buildFallFlaskBodyShellVertices(geom, wallThicknessPx) {
  var rings = buildFallFlaskBodyShellRings(geom, wallThicknessPx);
  if (!rings) return [];
  var inner = rings.inner;
  var o = rings.outer;
  return [
    inner[0], inner[1], inner[2], inner[3],
    o[3], o[2], o[1], o[0],
  ];
}

/**
 * Convex quads for the same shell: left wall, floor, right wall.
 * Matter `fromVertices` stays quiet without poly-decomp when each set is convex.
 * @returns {{ x: number, y: number }[][]}
 */
export function buildFallFlaskBodyShellConvexParts(geom, wallThicknessPx) {
  var rings = buildFallFlaskBodyShellRings(geom, wallThicknessPx);
  if (!rings) return [];
  var i = rings.inner;
  var o = rings.outer;
  return [
    [i[0], i[1], o[1], o[0]],
    [i[1], i[2], o[2], o[1]],
    [i[2], i[3], o[3], o[2]],
  ];
}

function verticesRelativeTo(verts, originX, originY) {
  var out = [];
  for (var i = 0; i < verts.length; i++) {
    out.push({ x: verts[i].x - originX, y: verts[i].y - originY });
  }
  return out;
}

/**
 * Stage I runway flask — three Matter static bodies:
 * 1. **Mouth** — one letterbox rect (`layoutFallFlaskMouthColliderRect`), label `flask_mouth`.
 * 2. **Neck** — one rect below the mouth band (`layoutFallFlaskNeckColliderRect`).
 * 3. **Body trapezoid** — open-topped shell (`buildFallFlaskBodyShellConvexParts` →
 *    three convex `fromVertices` quads compounded as `flask_wall`).
 */
export function createRunwayFlaskMatterColliders(Bodies, Body, geom, wallThicknessPx, wallOpts, partWallOpts) {
  var parts = partWallOpts || {};
  var mouthOpts = parts.mouth || wallOpts;
  var neckOpts = parts.neck || wallOpts;
  var shellOpts = parts.shell || wallOpts;

  var mouthRect = layoutFallFlaskMouthColliderRect(geom);
  var mouthBorder = mouthRect
    ? Bodies.rectangle(
      mouthRect.centerX,
      mouthRect.centerY,
      mouthRect.width,
      mouthRect.height,
      Object.assign({}, mouthOpts, { label: "flask_mouth" })
    )
    : null;

  var neckRect = layoutFallFlaskNeckColliderRect(geom);
  var neckSquare = neckRect
    ? Bodies.rectangle(
      neckRect.centerX,
      neckRect.centerY,
      neckRect.width,
      neckRect.height,
      Object.assign({}, neckOpts, { label: "flask_neck" })
    )
    : null;

  var bodyTop = geom.bodyTop != null ? geom.bodyTop : geom.neckBottom;
  var originY = (bodyTop + geom.bodyBottom) / 2;
  var shellQuads = buildFallFlaskBodyShellConvexParts(geom, wallThicknessPx);
  var shellParts = [];
  for (var qi = 0; qi < shellQuads.length; qi++) {
    var shellLocal = verticesRelativeTo(shellQuads[qi], geom.cx, originY);
    var shellPart = Bodies.fromVertices(geom.cx, originY, [shellLocal], {
      label: "flask_trapezoid_part",
    });
    if (shellPart) shellParts.push(shellPart);
  }
  var bodyTrapezoid = shellParts.length
    ? Body.create(Object.assign({}, shellOpts, { parts: shellParts, label: "flask_wall" }))
    : null;

  return { mouthBorder: mouthBorder, neckSquare: neckSquare, bodyTrapezoid: bodyTrapezoid };
}

/**
 * Single Matter rect for the mouth band (`T` … `mouthBottom`), letterbox `w` × `h`.
 * @returns {{ centerX: number, centerY: number, width: number, height: number }|null}
 */
export function layoutFallFlaskMouthColliderRect(geom) {
  var top = geom.T;
  var bottom = geom.mouthBottom != null ? geom.mouthBottom : top;
  var mouthH = bottom - top;
  if (!(mouthH > 0.5)) return null;
  var mouthLeft = geom.mouthLeft != null ? geom.mouthLeft : geom.neckLeft;
  var mouthRight = geom.mouthRight != null ? geom.mouthRight : geom.neckRight;
  var width = mouthRight - mouthLeft;
  if (!(width > 0.5)) return null;
  return {
    centerX: (mouthLeft + mouthRight) / 2,
    centerY: (top + bottom) / 2,
    width: width,
    height: mouthH,
  };
}

/**
 * Single Matter rect for the neck span (`mouthBottom` … `neckBottom`), letterbox `w`.
 * @returns {{ centerX: number, centerY: number, width: number, height: number }|null}
 */
export function layoutFallFlaskNeckColliderRect(geom) {
  var neckTop = geom.mouthBottom != null ? geom.mouthBottom : geom.T;
  // Shoulder (`bodyTop`) must be covered — letterbox `neck.h` can end above `trap.y`.
  var neckBottom = geom.bodyTop != null ? geom.bodyTop
    : (geom.neckBottom != null ? geom.neckBottom : geom.T);
  if (geom.neckBottom != null && geom.bodyTop != null) {
    neckBottom = Math.max(geom.neckBottom, geom.bodyTop);
  }
  var neckH = neckBottom - neckTop;
  if (!(neckH > 0.5)) return null;
  var neckLeft = geom.neckLeft;
  var neckRight = geom.neckRight;
  var width = neckRight - neckLeft;
  if (!(width > 0.5)) return null;
  return {
    centerX: (neckLeft + neckRight) / 2,
    centerY: (neckTop + neckBottom) / 2,
    width: width,
    height: neckH,
  };
}

/** @deprecated Use {@link layoutFallFlaskMouthColliderRect}. */
export function layoutFallFlaskMouthWallRects(geom, wallThicknessPx) {
  var r = layoutFallFlaskMouthColliderRect(geom);
  if (!r) return { left: null, right: null, top: null };
  return { left: r, right: null, top: null };
}

/** @deprecated Use {@link layoutFallFlaskNeckColliderRect}. */
export function layoutFallFlaskNeckWallRects(geom, wallThicknessPx) {
  var r = layoutFallFlaskNeckColliderRect(geom);
  if (!r) return { left: null, right: null };
  return { left: r, right: null };
}

/** @deprecated Use {@link buildFallFlaskBodyShellVertices}. */
export function buildFallFlaskShellVertices(geom, wallThicknessPx) {
  return buildFallFlaskBodyShellVertices(geom, wallThicknessPx);
}

/**
 * Dev overlay: neck interior rect + body trapezoid (centerline, not Matter hulls).
 */
export function drawFallFlaskShapeGuide(ctx, geom, style) {
  if (!ctx || !geom) return;
  var cfg = style || {};
  var lw = cfg.lineWidth != null ? cfg.lineWidth : 1.5;
  var neckBottom = geom.neckBottom != null ? geom.neckBottom : geom.bodyTop;
  var neckLeft = geom.neckLeft;
  var neckRight = geom.neckRight;
  var T = geom.T;
  var mouthBottom = geom.mouthBottom != null ? geom.mouthBottom : T;
  var mouthLeft = geom.mouthLeft != null ? geom.mouthLeft : neckLeft;
  var mouthRight = geom.mouthRight != null ? geom.mouthRight : neckRight;
  var bodyTop = geom.bodyTop;
  var fL = geom.floorLeft;
  var fR = geom.floorRight;
  var B = geom.bodyBottom;
  var shoulderLeft = geom.shoulderLeft != null ? geom.shoulderLeft : neckLeft;
  var shoulderRight = geom.shoulderRight != null ? geom.shoulderRight : neckRight;

  ctx.save();
  ctx.lineWidth = lw;
  ctx.setLineDash([4, 3]);

  ctx.strokeStyle = cfg.mouthStroke || cfg.neckStroke || "rgba(248, 113, 113, 0.9)";
  ctx.strokeRect(mouthLeft, T, mouthRight - mouthLeft, Math.max(0, mouthBottom - T));

  if (geom.collectLeft != null && geom.collectRight != null
    && geom.collectTop != null && geom.collectBottom != null) {
    ctx.strokeStyle = cfg.collectStroke || "rgba(34, 197, 94, 0.85)";
    ctx.strokeRect(
      geom.collectLeft,
      geom.collectTop,
      geom.collectRight - geom.collectLeft,
      Math.max(0, geom.collectBottom - geom.collectTop)
    );
  }

  ctx.strokeStyle = cfg.neckStroke || "rgba(248, 113, 113, 0.9)";
  ctx.strokeRect(neckLeft, mouthBottom, neckRight - neckLeft, neckBottom - mouthBottom);

  ctx.strokeStyle = cfg.bodyStroke || "rgba(248, 113, 113, 0.9)";
  ctx.beginPath();
  ctx.moveTo(shoulderLeft, bodyTop);
  ctx.lineTo(fL, B);
  ctx.lineTo(fR, B);
  ctx.lineTo(shoulderRight, bodyTop);
  ctx.closePath();
  ctx.stroke();

  ctx.restore();
}